<template>
  <Teleport to="body">
    <template v-if="isOpen">
      <div
        :id="modalId || undefined"
        role="dialog"
        class="modal fade show"
        :class="rootClass"
        style="display: block;"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="bodyId"
        @click.self="onBackdropClick"
      >
        <div class="modal-dialog" :class="dialogClasses">
          <div :id="contentId" tabindex="-1" class="modal-content">
            <header v-if="showHeader" :id="headerId" class="modal-header">
              <h5 :id="titleId" class="modal-title">
                <slot name="modal-title">
                  <span v-if="title">{{ title }}</span>
                </slot>
              </h5>
              <slot name="modal-header-close">
                <button
                  v-if="!hideHeaderClose"
                  type="button"
                  class="close"
                  aria-label="Close"
                  @click="onClose"
                >×</button>
              </slot>
            </header>
            <div :id="bodyId" class="modal-body">
              <slot />
            </div>
            <div v-if="showFooter" class="modal-footer">
              <slot name="modal-footer" />
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" />
    </template>
  </Teleport>
</template>

<script>
import { bvModals } from '@/plugins/bvModal'

const SIZE_CLASS = {
  sm: 'modal-sm',
  md: '',
  lg: 'modal-lg',
  xl: 'modal-xl'
}

function syncBodyModalOpenState () {
  const anyOpen = Object.values(bvModals).some(Boolean)
  document.body.classList.toggle('modal-open', anyOpen)
}

function removeOrphanModalBackdrops () {
  const anyOpen = Object.values(bvModals).some(Boolean)
  if (!anyOpen) {
    document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove())
    document.body.classList.remove('modal-open')
  }
}

export default {
  name: 'BModal',
  inheritAttrs: false,
  props: {
    id: { type: String, default: '' },
    title: { type: String, default: '' },
    size: { type: String, default: 'md' },
    hideFooter: { type: Boolean, default: false },
    okOnly: { type: Boolean, default: false },
    noCloseOnBackdrop: { type: Boolean, default: false },
    noCloseOnEsc: { type: Boolean, default: false },
    hideHeaderClose: { type: Boolean, default: false },
    centered: { type: Boolean, default: false },
    dialogClass: { type: [String, Array, Object], default: null }
  },
  emits: ['close', 'hidden', 'shown'],
  data () {
    return { localVisible: false }
  },
  computed: {
    modalId () {
      return this.id || this.$attrs.id || ''
    },
    isOpen () {
      if (this.modalId && Object.prototype.hasOwnProperty.call(bvModals, this.modalId)) {
        return !!bvModals[this.modalId]
      }
      return this.localVisible
    },
    rootClass () {
      return this.$attrs.class
    },
    dialogClasses () {
      return [
        SIZE_CLASS[this.size] || '',
        this.dialogClass,
        { 'modal-dialog-centered': this.centered }
      ]
    },
    showHeader () {
      return !!(this.title || this.$slots['modal-title'] || !this.hideHeaderClose || this.$slots['modal-header-close'])
    },
    showFooter () {
      return !this.hideFooter && !!(this.$slots['modal-footer'] || this.okOnly)
    },
    contentId () {
      return this.modalId ? `${this.modalId}___BV_modal_content_` : undefined
    },
    headerId () {
      return this.modalId ? `${this.modalId}___BV_modal_header_` : undefined
    },
    titleId () {
      return this.modalId ? `${this.modalId}___BV_modal_title_` : undefined
    },
    bodyId () {
      return this.modalId ? `${this.modalId}___BV_modal_body_` : undefined
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler (open) {
        syncBodyModalOpenState()
        if (open) {
          document.addEventListener('keydown', this.onKeydown)
          this.$emit('shown')
        } else {
          document.removeEventListener('keydown', this.onKeydown)
          removeOrphanModalBackdrops()
        }
      }
    }
  },
  beforeUnmount () {
    if (this.modalId) bvModals[this.modalId] = false
    document.removeEventListener('keydown', this.onKeydown)
    syncBodyModalOpenState()
    removeOrphanModalBackdrops()
  },
  methods: {
    setVisible (value) {
      if (value) this.show()
      else this.hide()
    },
    show () {
      if (this.modalId) bvModals[this.modalId] = true
      this.localVisible = true
    },
    hide () {
      if (!this.isOpen) return
      if (this.modalId) bvModals[this.modalId] = false
      this.localVisible = false
      this.$emit('close')
      this.$emit('hidden')
    },
    onClose () {
      this.hide()
    },
    onBackdropClick () {
      if (!this.noCloseOnBackdrop) this.hide()
    },
    onKeydown (event) {
      if (!this.noCloseOnEsc && event.key === 'Escape') this.hide()
    }
  }
}
</script>
